/**
 * Shared instruction-message definitions for the `return-shipping-instructions`
 * block, used by the email renderer (to pick the right default message) and the
 * config panel (for placeholders / the "return slip" dropdown entry).
 *
 * Several carrier codes map to the same message (e.g. all UPS variants → `ups`),
 * mirroring the legacy `return-request-label-created` email `descriptionsMap`.
 * `unknownCarrier` is the generic default for any carrier code without a
 * dedicated message — the `customerCarrier` copy is reserved for the actual
 * `customer-carrier` code (fixing a bug replicated from the legacy email).
 */

export type InstructionKey =
  | "colissimo"
  | "chronoShop2Shop"
  | "ups"
  | "customerCarrier"
  | "merchantCarrier"
  | "returnToStore"
  | "malca"
  | "tnt"
  | "bonDeRetour"
  | "unknownCarrier";

export type InstructionDefinition = {
  id: InstructionKey;
  /** Key in the block `tLayout` dictionary (`shippingInstructions.*`). */
  tLayoutKey: string;
  /** English fallback (tLayout has no per-key fallback). */
  fallback: string;
  /** i18n key for the dropdown option label (editor-blocks-library namespace). */
  labelKey: string;
  /** Fallback label for the dropdown option. */
  labelFallback: string;
};

const LABEL_KEY = (id: string) =>
  `editorBlocksLibrary.blocks.return-shipping-instructions.panel.message.${id}`;

export const INSTRUCTION_KEYS: InstructionDefinition[] = [
  {
    id: "colissimo",
    tLayoutKey: "shippingInstructions.colissimo",
    fallback:
      "You can now print your return label and drop off your package at the nearest Colissimo relay point.",
    labelKey: LABEL_KEY("colissimo"),
    labelFallback: "Colissimo",
  },
  {
    id: "chronoShop2Shop",
    tLayoutKey: "shippingInstructions.chronoShop2Shop",
    fallback:
      "You can now print your return label and drop off your package at the nearest Chronopost relay point.",
    labelKey: LABEL_KEY("chronoShop2Shop"),
    labelFallback: "Chronopost / pickup point",
  },
  {
    id: "ups",
    tLayoutKey: "shippingInstructions.ups",
    fallback:
      "You can now print your return label and drop off your package at the nearest UPS relay point.",
    labelKey: LABEL_KEY("ups"),
    labelFallback: "UPS",
  },
  {
    id: "customerCarrier",
    tLayoutKey: "shippingInstructions.customerCarrier",
    fallback: "You can now send your return package to the carrier of your choice.",
    labelKey: LABEL_KEY("customerCarrier"),
    labelFallback: "Customer's carrier",
  },
  {
    id: "merchantCarrier",
    tLayoutKey: "shippingInstructions.merchantCarrier",
    fallback: "We will send you a return label by email within 1 to 3 business days.",
    labelKey: LABEL_KEY("merchantCarrier"),
    labelFallback: "Merchant's carrier",
  },
  {
    id: "returnToStore",
    tLayoutKey: "shippingInstructions.returnToStore",
    fallback:
      "You can now print your return label and drop off your package at the nearest store.",
    labelKey: LABEL_KEY("returnToStore"),
    labelFallback: "Return to store",
  },
  {
    id: "malca",
    tLayoutKey: "shippingInstructions.malca",
    fallback:
      "You can now print your return label and drop off your package at the nearest Malca relay point.",
    labelKey: LABEL_KEY("malca"),
    labelFallback: "Malca",
  },
  {
    id: "tnt",
    tLayoutKey: "shippingInstructions.tnt",
    fallback:
      "You can now print your return label and drop off your package at the nearest TNT relay point.",
    labelKey: LABEL_KEY("tnt"),
    labelFallback: "TNT",
  },
  {
    id: "bonDeRetour",
    tLayoutKey: "shippingInstructions.bonDeRetour",
    fallback:
      "Your return slip is ready. You can now print it and use it for your return.",
    labelKey: LABEL_KEY("bonDeRetour"),
    labelFallback: "Return slip",
  },
  {
    id: "unknownCarrier",
    tLayoutKey: "shippingInstructions.unknownCarrier",
    fallback:
      "You can now print your return label and ship your package with the carrier indicated on the label.",
    labelKey: LABEL_KEY("unknownCarrier"),
    labelFallback: "Other carriers",
  },
];

export const INSTRUCTION_BY_ID: Record<InstructionKey, InstructionDefinition> =
  INSTRUCTION_KEYS.reduce(
    (acc, def) => {
      acc[def.id] = def;
      return acc;
    },
    {} as Record<InstructionKey, InstructionDefinition>,
  );

/** Maps a raw carrier code to its default instruction message. */
export const CARRIER_TO_INSTRUCTION: Record<string, InstructionKey> = {
  colissimo: "colissimo",
  "chrono-shop2shop": "chronoShop2Shop",
  crosslog: "chronoShop2Shop",
  ups: "ups",
  "ups-saver": "ups",
  "ups-standard": "ups",
  "ups-express": "ups",
  "fake-dpd": "ups",
  "customer-carrier": "customerCarrier",
  "merchant-carrier": "merchantCarrier",
  "return-to-store": "returnToStore",
  malca: "malca",
  tnt: "tnt",
};

/**
 * Content key for the logistic return-slip override. Overrides are otherwise
 * keyed by the raw shop carrier code; this fixed key covers the return-slip
 * case (`hasLogisticLabel`), which is carrier-independent.
 */
export const BON_DE_RETOUR_CONTENT_KEY = "bonDeRetour";

/**
 * Keep blank lines from collapsing in the email. The rich-text editor emits an
 * empty paragraph (`<p ...></p>`) for a blank line, which renders at zero height
 * once its margins are stripped — so the line vanishes. Injecting a `&nbsp;`
 * gives it height, mirroring the paragraph block's workaround.
 */
export function preserveBlankLines(html: string): string {
  return html.replace(/<p([^>]*)><\/p>/g, "<p$1>&nbsp;</p>");
}

/**
 * True when an HTML override is "visually empty" — the rich-text editor emits
 * `<p></p>` (not `""`) when cleared, which must be treated as "no override" so
 * the default message is used.
 */
export function isEmptyHtml(html?: string | null): boolean {
  if (!html) return true;
  const stripped = html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return stripped.length === 0;
}

/**
 * Picks the DEFAULT instruction message for a shipment: a logistic return slip
 * overrides everything; otherwise pick by carrier code, falling back to the
 * generic `unknownCarrier` copy for unknown / missing carriers (also the editor
 * preview case). The `customerCarrier` copy is reserved for the actual
 * `customer-carrier` code via `CARRIER_TO_INSTRUCTION`.
 */
export function resolveInstructionKey(
  carrierCode?: string,
  hasLogisticLabel?: boolean,
): InstructionKey {
  if (hasLogisticLabel) return "bonDeRetour";
  return (carrierCode && CARRIER_TO_INSTRUCTION[carrierCode]) || "unknownCarrier";
}

/** Per-locale, per-key HTML override map, as stored on the block content. */
export type ReturnInstructionsOverrideContent = Record<
  string,
  Partial<Record<string, string>>
>;

/**
 * Content keys to try for a merchant override, most specific first. The raw
 * carrier code is the current scheme (one override per shop carrier); the legacy
 * instruction key preserves overrides saved before content was keyed by raw code
 * (e.g. an override under `ups` still applies to `ups-express`). The return-slip
 * case uses a single carrier-independent key.
 */
export function getOverrideContentKeys(
  carrierCode?: string,
  hasLogisticLabel?: boolean,
): string[] {
  if (hasLogisticLabel) return [BON_DE_RETOUR_CONTENT_KEY];
  if (!carrierCode) return [];
  const legacy = CARRIER_TO_INSTRUCTION[carrierCode];
  return legacy && legacy !== carrierCode ? [carrierCode, legacy] : [carrierCode];
}

/**
 * Resolves the merchant HTML override for a shipment, or `undefined` when none
 * applies (so the caller renders the default). Tries each candidate key in the
 * current locale then the default locale, skipping visually-empty HTML.
 */
export function resolveOverride(
  content: ReturnInstructionsOverrideContent | undefined,
  locale: string,
  defaultLocale: string,
  carrierCode?: string,
  hasLogisticLabel?: boolean,
): string | undefined {
  if (!content) return undefined;
  for (const key of getOverrideContentKeys(carrierCode, hasLogisticLabel)) {
    const localeOverride = content[locale]?.[key];
    if (!isEmptyHtml(localeOverride)) return localeOverride ?? undefined;
    const defaultOverride = content[defaultLocale]?.[key];
    if (!isEmptyHtml(defaultOverride)) return defaultOverride ?? undefined;
  }
  return undefined;
}
